import { useEffect, useState } from "react";
import { api, API, RAYG, CLASSIFICATIONS, authHeaders } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { FilePdf, Presentation, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const REPORTS = [
  ["Enterprise Engineering Diagnostic (PDF)", "Scores, workflow analysis, scenarios, business case, recommended model and full roadmap", "pdf"],
  ["Board Deck (PowerPoint)", "10-slide board-ready deck: enterprise map, heat maps, workflows, business case, operating model and roadmap matrix", "pptx"],
  ["Analyst Workbook (Excel)", "13-sheet workbook: departments, capabilities, workflow steps, roles & tasks, systems, data, controls, diagnostics, business case, roadmap and benefits", "xlsx"],
  ["Current-State Enterprise Map", "Included in all exports", null],
  ["Workflow and Handoff Analysis", "Included in all exports", null],
  ["Recommended Integrated Operating Model", "Included when an AI draft has been generated", null],
  ["Transformation Roadmap and Business Case", "Included as the six-phase matrix and costed comparison", null],
];

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { api.get("/dashboard/summary").then((r) => setSummary(r.data)); }, []);

  const download = async (kind) => {
    setDownloading(kind);
    const [path, filename, label] = kind === "pptx"
      ? ["/reports/board-deck", "enterprise_board_deck.pptx", "Board deck (PPTX)"]
      : kind === "xlsx"
        ? ["/reports/excel-workbook", "enterprise_navigator_workbook.xlsx", "Analyst workbook (Excel)"]
        : ["/reports/executive-pdf", "enterprise_engineering_diagnostic.pdf", "Executive PDF"];
    try {
      const res = await fetch(`${API}${path}`, { credentials: "include", headers: authHeaders() });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
    setDownloading(null);
  };

  const flagData = summary ? Object.entries(summary.flags).map(([k, v]) => ({ name: summary.flag_labels[k] || k, count: v })) : [];
  const clsData = summary ? Object.entries(summary.classification_counts).map(([k, v]) => ({ name: CLASSIFICATIONS[k]?.label || k, count: v, color: CLASSIFICATIONS[k]?.color })) : [];

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="reports-page">
      <PageHeader overline="Executive Dashboard & Reports" title="Reports & Exports"
        subtitle="Board-ready outputs with a one-page executive summary and evidence detail."
        actions={
          <>
            <button onClick={() => download("xlsx")} disabled={downloading !== null} data-testid="download-xlsx-btn"
              className="flex items-center gap-2 text-xs border border-[#0A0A0A] text-[#0A0A0A] rounded-sm px-4 py-2.5 font-semibold hover:bg-black/5 transition-colors disabled:opacity-50">
              <FileXls size={15} /> {downloading === "xlsx" ? "Preparing…" : "Export Workbook (XLSX)"}</button>
            <button onClick={() => download("pptx")} disabled={downloading !== null} data-testid="download-pptx-btn"
              className="flex items-center gap-2 text-xs bg-[#D4AF37] text-[#0A0A0A] rounded-sm px-4 py-2.5 font-semibold hover:bg-[#e0c258] transition-colors disabled:opacity-50">
              <Presentation size={15} /> {downloading === "pptx" ? "Preparing…" : "Export Board Deck (PPTX)"}</button>
            <button onClick={() => download("pdf")} disabled={downloading !== null} data-testid="download-pdf-btn"
              className="flex items-center gap-2 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2.5 font-semibold hover:bg-black/85 transition-colors disabled:opacity-50">
              <FilePdf size={15} /> {downloading === "pdf" ? "Preparing…" : "Export Executive PDF"}</button>
          </>} />
      {summary && (
        <div className="px-8 grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[["Departments", summary.departments], ["Capabilities", summary.capabilities], ["Workflows", summary.workflows], ["Roadmap initiatives", summary.initiatives]].map(([l, v]) => (
            <div key={l} className="bg-white border border-black/10 rounded-sm p-5" data-testid="report-stat-card">
              <span className="font-display text-4xl font-black text-[#0A0A0A]">{v}</span>
              <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mt-1">{l}</p>
            </div>
          ))}
        </div>
      )}
      <div className="px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10 rounded-sm p-5">
          <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Workflow issues detected (all workflows)</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={flagData} layout="vertical" margin={{ left: 60 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "#0000000a" }} />
              <Bar dataKey="count" fill="#EF4444" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-black/10 rounded-sm p-5">
          <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Work-redesign classification of workflow steps</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={clsData} layout="vertical" margin={{ left: 60 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "#0000000a" }} />
              <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                {clsData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="px-8 mt-6">
        <div className="bg-white border border-black/10 rounded-sm divide-y divide-black/5">
          {REPORTS.map(([name, desc, kind]) => (
            <div key={name} className="flex items-center justify-between px-5 py-4" data-testid="report-list-item">
              <div>
                <p className="font-medium text-[14px]">{name}</p>
                <p className="text-[12px] text-black/50">{desc}</p>
              </div>
              {kind ? (
                <button onClick={() => download(kind)} disabled={downloading !== null}
                  className="text-xs border border-[#D4AF37] text-[#B8962E] rounded-sm px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50"
                  data-testid={`report-download-${kind}`}>Download {kind.toUpperCase()}</button>
              ) : (
                <span className="text-[11px] text-black/40">In both exports</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-black/45 mt-3">All three export formats are generated live from workspace data.</p>
      </div>
    </div>
  );
}
